/**
 * The catalog's default copy (the rule is in `shared.ts`): its heading, its place under Home, its
 * tabs, its category chips, its filters, and the grid's headings and empty states.
 */
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { STOREFRONT_FRAME_COPY } from "./frame.js";

/** The catalog's tabs. */
export type CatalogTab = "all" | "new" | "favorites";

/** The catalog's sorts, in the order its sort control lists them: the design system's `BoxSort`. */
export const CATALOG_SORTS = ["default", "price-asc", "price-desc", "popular"] as const;
export type CatalogSort = (typeof CATALOG_SORTS)[number];

/** The catalog page's words, by section, in the store's word for its boxes and its currency. */
export interface CatalogCopy {
  breadcrumb: { home: string };
  /** The page's heading, and its place in the breadcrumb under `home`. */
  title: string;
  /** `label` is the tab list's accessible name. */
  tabs: { label: string; options: Record<CatalogTab, string> };
  /** The box category chips: the row's accessible name, and the chip that shows every box. */
  categories: { label: string; all: string };
  filters: {
    /** Each field's accessible name, and the hint it shows while empty. */
    search: { label: string; placeholder: string };
    priceMin: { label: string; placeholder: string };
    priceMax: { label: string; placeholder: string };
    sort: { label: string; options: Record<CatalogSort, string> };
    /** The switch that keeps only the boxes the shopper's balance covers. */
    affordable: string;
    clear: string;
    /**
     * On a phone, the filters wait in a sheet: the floating button that opens it, the button's
     * name with how many filters are set (it holds the visible label, WCAG 2.5.3), the sheet's
     * heading, and its close (the filters apply as they change).
     */
    sheet: { label: string; countLabel: (count: number) => string; title: string; done: string };
  };
  grid: {
    /** The grid's heading on each tab. */
    title: Record<CatalogTab, string>;
    /** The grid's heading on each tab while a box category is picked, given its name. */
    titleIn: Record<CatalogTab, (category: string) => string>;
    /** Why the grid shows nothing. */
    empty: {
      /** Favorites, signed out: the message, and the label of its link to sign in. */
      signIn: { message: string; action: string };
      /** The store lists no boxes at all. */
      noBoxes: string;
      noMatches: string;
      noFavorites: string;
      /** Nothing was added in the `days` a box counts as new. */
      noNew: (days: number) => string;
      /** The same, while a box category is picked, given its name. */
      inCategory: {
        noBoxes: (category: string) => string;
        noMatches: (category: string) => string;
        noFavorites: (category: string) => string;
        noNew: (category: string, days: number) => string;
      };
    };
  };
}

export function defaultCatalogCopy({
  terms,
  currencyPlural,
}: {
  terms: StorefrontTerminology;
  /** The currency's plural name, or its name when it has none. */
  currencyPlural: string;
}): CatalogCopy {
  const { box } = terms;
  return {
    breadcrumb: { home: STOREFRONT_FRAME_COPY.home },
    title: capitalizeTerm(box.plural),
    tabs: {
      label: `Which ${box.plural} to show`,
      options: { all: "All", new: "New", favorites: "Favorites" },
    },
    categories: { label: `${capitalizeTerm(box.singular)} categories`, all: "All categories" },
    filters: {
      search: {
        label: `Search ${box.plural}`,
        placeholder: `Search ${capitalizeTerm(box.plural)}`,
      },
      priceMin: { label: "Minimum price", placeholder: "Min" },
      priceMax: { label: "Maximum price", placeholder: "Max" },
      sort: {
        label: "Sort",
        options: {
          default: "Default",
          "price-asc": "Price: low to high",
          "price-desc": "Price: high to low",
          popular: "Most opened",
        },
      },
      affordable: `Enough ${currencyPlural}`,
      clear: "Clear",
      sheet: {
        label: "Filters",
        countLabel: (count) => (count === 0 ? "Filters" : `Filters, ${count} active`),
        title: "Filters",
        done: "Done",
      },
    },
    grid: {
      title: {
        all: capitalizeTerm(`All ${box.plural}`),
        new: capitalizeTerm(`New ${box.plural}`),
        favorites: capitalizeTerm(`Favorite ${box.plural}`),
      },
      titleIn: {
        all: (category) => `${category} ${box.plural}`,
        new: (category) => `New ${category} ${box.plural}`,
        favorites: (category) => `Favorite ${category} ${box.plural}`,
      },
      empty: {
        signIn: { message: `Sign in to see the ${box.plural} you save.`, action: "Sign in" },
        noBoxes: `No active ${box.plural} in this environment yet — publish one in the dashboard.`,
        noMatches: `No ${box.plural} match these filters.`,
        noFavorites: `Press the heart on any ${box.singular} to save it here.`,
        noNew: (days) => `No ${box.plural} were added in the last ${days} days.`,
        inCategory: {
          noBoxes: (category) => `No ${box.plural} in ${category} right now.`,
          noMatches: (category) => `No ${box.plural} in ${category} match these filters.`,
          noFavorites: (category) => `None of the ${box.plural} you saved are in ${category}.`,
          noNew: (category, days) =>
            `No ${box.plural} in ${category} were added in the last ${days} days.`,
        },
      },
    },
  };
}
